<script>
import { useAlert } from 'dashboard/composables';
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';

export default {
  name: 'SelectCustomer',

  props: {
    // Shows the red required asterisk and, with `invalid`, a red border.
    required: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
  },

  data() {
    return {
      isDropdownOpen: false,
      contactQuery: '',
      chatList: [],

      contactCurrentPage: 1,
      contactTotalPages: 1,
      contactNextPage: false,

      contactCount: 0,
      contactPerPage: 10,
    };
  },

  computed: {
    getCurrentAccountId() {
      return this.$store.getters['getCurrentAccountId'];
    },
  },

  watch: {
    contactQuery() {
      this.contactCurrentPage = 1;
      this.fetchChatLists();
    },
  },

  async mounted() {
    this.fetchChatLists();
  },

  methods: {
    showInboxLogo(inbox) {
      switch(inbox) {
        case 'website':
          return 'globe';
        case 'facebook':
          return 'brand-facebook'; 
        case 'twitter':
          return 'brand-twitter';
        case 'whatsapp':
          return 'brand-whatsapp';
        case 'api':
          return 'code';
        case 'email':
          return 'mail';
        case 'telegram':
          return 'brand-telegram';
        case 'line':
          return 'brand-line';
        case 'sms':
          return 'brand-sms';
        case 'phone':
          return 'call';
        case 'instagram':
          return 'brand-instagram';
        default:
          return 'globe';
      }
    },

    selectCustomer(customer) {
      this.contactQuery = customer.name;
      this.$emit('select-customer', customer);
      this.isDropdownOpen = false;
    },

    closeDropdownWithDelay() {
      setTimeout(() => {
        this.isDropdownOpen = false;
      }, 150);
    },

    prevPage() {
      if (this.contactCurrentPage > 1) {
        this.contactCurrentPage--;
        this.fetchChatLists();
      }
    },

    nextPage() {
      if (this.contactNextPage) {
        this.contactCurrentPage++;
        this.fetchChatLists();
      }
    },

    async fetchChatLists() {
      try {
        const url = new URL(
          `${window.location.origin}/api/v1/accounts/${this.getCurrentAccountId}/appointments/contacts`
        );

        url.searchParams.append('page', this.contactCurrentPage);
        url.searchParams.append('q', this.contactQuery);

        const rawResponse = await fetch(url.toString(), {
          headers: {
            ...getCentralHeaders(),
          },
        });

        if (!rawResponse.ok) {
          const errorData = await rawResponse.json();
          throw new Error(errorData.detail);
        }

        const response = await rawResponse.json();

        this.chatList = response.contacts || [];

        this.contactCount = response.meta.count;
        this.contactPerPage = response.meta.per_page;
        this.contactTotalPages = response.meta.total_pages;
        this.contactNextPage = response.meta.next;
      } catch (err) {
        console.error('Error fetching contacts:', err);
        useAlert(this.$t('APPOINTMENTS.CREATE_MODAL.ERRORS.CONTACTS_LOAD_ERROR'));
        this.chatList = [];
      }
    },
  },
};
</script>

<template>
  <label class="block relative mb-4">
    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
      {{ $t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER') }}
      <span v-if="required" class="text-red-500">*</span>
    </span>

    <div
      class="mt-1 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus-within:ring-2 focus-within:ring-woot-500"
      :class="{ '!border-red-500': invalid }"
    >
      <input
        type="text"
        v-model="contactQuery"
        class="reset-base w-full p-2 bg-transparent border-none focus:outline-none"
        :placeholder="$t('APPOINTMENTS.CREATE_MODAL.SELECT_CUSTOMER')"
        @focus="isDropdownOpen = true"
        @blur="closeDropdownWithDelay"
        @click="isDropdownOpen = true"
      />

      <!-- Backdrop -->
      <div
        v-if="isDropdownOpen"
        class="fixed inset-0 z-40"
        @click="isDropdownOpen = false"
      ></div>

      <!-- Dropdown -->
      <div
        v-if="isDropdownOpen"
        class="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-700 border rounded-md shadow-lg max-h-60 overflow-y-auto"
      >
        <div v-if="chatList.length === 0" class="p-2 text-slate-500">
          {{ $t('APPOINTMENTS.CREATE_MODAL.NO_CUSTOMERS') }}
        </div>

        <div v-else>
          <div
            v-for="customer in chatList"
            :key="customer.id"
            class="p-3 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer customer-option border-b border-slate-200 dark:border-slate-700"
            @click.prevent="selectCustomer(customer)"
          >
            <div class="text-sm font-medium text-slate-900 dark:text-white flex gap-1 items-center">
              <fluent-icon
                v-if="customer.conversations && customer.conversations.length !== 0"
                size="14"
                :icon="showInboxLogo(customer.conversations[0].inbox_channel)"
              />

              {{ customer.name }}
              <span
                v-if="customer.phone_number"
                class="text-xs text-slate-500 dark:text-slate-400"
              >
                ({{ customer.phone_number }})
              </span>
            </div>

            <div
              class="text-xs text-slate-500 dark:text-slate-400"
              v-if="customer.conversations && customer.conversations.length !== 0"
            >
              {{ customer.conversations[0].inbox_name || '—' }}
            </div>
          </div>

          <!-- Pagination -->
          <div class="flex items-center justify-between p-2 border-t">
            <button
              type="button"
              class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
              @mousedown.prevent="prevPage"
              :disabled="contactCurrentPage === 1"
            >
              Previous
            </button>

            <span class="text-sm text-slate-600 dark:text-slate-400">
              {{ contactCurrentPage }} / {{ contactTotalPages }}
            </span>

            <button
              type="button"
              class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
              @mousedown.prevent="nextPage"
              :disabled="!contactNextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </label>
</template>
